import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {distinctUntilChanged} from 'rxjs/operators';
import {SegmentModel} from '../../segment/segment.model';
import {segmentListSelector} from '../../segment/segment.reducer';
import {AppStore} from '../../store/store';
import {ACTIVE_TARGET_SYSTEMS, TargetSystemModel} from '../../target/target-system.model';
import {UserModel} from '../../user/user.model';
import {userSelector} from '../../user/user.reducer';
import {AuthorizationUtils} from '../../utils/authorization.utils';
import {DistributionAccountCreateRequestModel} from '../distribution-account.model';
import {
  distributionAccountCreated,
  distributionAccountErrorSelector,
  targetSystemListSelector
} from '../distribution-account.reducer';
import {DistributionAccountService} from '../distribution-account.service';
import {SubSink} from 'subsink';

@Component({
  selector: 'app-distribution-account-create',
  templateUrl: 'create.component.html'
})
export class DistributionAccountCreateComponent implements OnInit, OnDestroy {
  segmentIdDisabled = false;
  errorMessage!: string;
  user!: UserModel;
  subSink = new SubSink();
  isAdmin = false;
  targetSystemIdControl: FormControl = new FormControl('', Validators.required);
  segmentIdControl: FormControl = new FormControl('', Validators.required);

  allSegments: SegmentModel[] = [];
  allTargetSystems: TargetSystemModel[] = [];

  distributionAccountCreateForm: FormGroup = new FormGroup({
    partnerId: new FormControl(''),
    description: new FormControl('', Validators.required),
    targetSystemId: this.targetSystemIdControl,
    foreignAccountId: new FormControl('', Validators.required),
    visibleForAll: new FormControl(false),
    initialSegmentMapping: new FormGroup({
      segmentId: this.segmentIdControl,
      foreignId: new FormControl('')
    })
  });

  constructor(private appStore: AppStore,
              private matDialog: MatDialog,
              private dialogRef: MatDialogRef<DistributionAccountCreateComponent>,
              private distributionAccountActions: DistributionAccountService) {
  }

  ngOnInit() {
    const appState = this.appStore.getState();
    this.user = userSelector(appState);
    this.reset();
    this.isAdmin = AuthorizationUtils.isAdmin(this.user);

    this.allSegments = segmentListSelector(appState) || [];
    this.extractTargetSystems(appState);

    this.subSink.sink = this.appStore.subscribe((state: AppStore) => {
      const segmentList = segmentListSelector(state) || [];

      if (this.allSegments.length === 0 || segmentList.length > 0) {
        this.allSegments = segmentList;
      }
      this.extractTargetSystems(state);

      if (distributionAccountCreated(state)) {
        this.dialogRef.close();
      } else {
        const distributionAccountErrorState = distributionAccountErrorSelector(state);

        if (distributionAccountErrorState) {
          this.errorMessage = `${distributionAccountErrorState.message}`;
        }
      }
    });

    this.subSink.sink = this.targetSystemIdControl.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((targetSystemId) => {
        const targetSystem = this.allTargetSystems.find((target) => {
          return target.id === targetSystemId;
        });

        if (targetSystem !== undefined) {
          this.segmentIdDisabled = targetSystem.userProvidedForeignId;
          const foreignId = this.distributionAccountCreateForm?.get('initialSegmentMapping')?.get('foreignId');
          if (this.segmentIdDisabled) {
            foreignId?.enable();
            foreignId?.setValidators([Validators.required]);
          } else {
            foreignId?.disable();
            foreignId?.clearValidators();
          }
        }
      });
  }

  ngOnDestroy() {
    this.subSink.unsubscribe();
  }

  reset() {
    this.clear();
    this.distributionAccountCreateForm.reset(
      new DistributionAccountCreateRequestModel(
        {partnerId: this.user.partnerId} as DistributionAccountCreateRequestModel
      )
    );
  }

  clear() {
    this.errorMessage = '';
  }

  extractTargetSystems(state: AppStore) {
    this.allTargetSystems = targetSystemListSelector(state)
      .filter((it: TargetSystemModel) => it.userProvidedForeignId || ACTIVE_TARGET_SYSTEMS.indexOf(it.id) > -1)
      .sort((a: TargetSystemModel, b: TargetSystemModel) => a.id - b.id);
  }

  create() {
    this.clear();

    if (this.distributionAccountCreateForm.valid) {
      const distributionAccountObject = new DistributionAccountCreateRequestModel(this.distributionAccountCreateForm.value);
      this.distributionAccountActions.createDistributionAccount(distributionAccountObject);
    }
  }
}
